/**
 * InfoPilot Explorer - Extended Joke Library
 * Dry, witty, satirical humor inspired by "Letters to Evelyn" by John Selman
 * Themes: escaping murder attempts, elite fighter pilot's son, Vietnam war irreverence,
 * military aviation, absurd bureaucracy, and the cosmic joke of survival
 */

// =====================================================
// COPYABLE JOKES - Dry, Witty, Satirical, Gut-Busting
// =====================================================

export const JOKES_LIBRARY = [
  // === THE SON OF AN ELITE FIGHTER PILOT ===
  {
    id: 'fp1',
    category: 'Fighter Pilot',
    text: "My father flew A-4 Skyhawks and A-7 Corsair IIs on 298 bombing missions over Vietnam. I fly economy class to Tulsa. The apple didn't fall far from the tree — it rolled into a completely different orchard, got lost, and had to use Google Maps.",
    tags: ['aviation', 'family', 'dry']
  },
  {
    id: 'fp2',
    category: 'Fighter Pilot',
    text: "People ask what it's like being the son of a decorated fighter pilot. I tell them it's a lot like being the son of a regular person, except Thanksgiving dinner comes with a debriefing.",
    tags: ['aviation', 'family', 'dry']
  },
  {
    id: 'fp3',
    category: 'Fighter Pilot',
    text: "My old man pulled 7 Gs in combat. I pulled a hamstring getting off the couch. Genetics are fascinating.",
    tags: ['aviation', 'physical', 'dry']
  },
  {
    id: 'fp4',
    category: 'Fighter Pilot',
    text: "Dad said the trick to surviving enemy fire is staying calm under pressure. I use the same technique when the WiFi goes out.",
    tags: ['aviation', 'modern', 'dry']
  },
  {
    id: 'fp5',
    category: 'Fighter Pilot',
    text: "They gave my father a medal for flying through triple-A fire. They gave me a participation trophy for showing up to softball practice. The bar has been adjusted.",
    tags: ['aviation', 'generational', 'satirical']
  },

  // === ESCAPING MURDER ATTEMPTS ===
  {
    id: 'ma1',
    category: 'Survival',
    text: "I've survived three murder attempts. The first was a car accident that wasn't. The second was food poisoning that was. The third was a performance review.",
    tags: ['survival', 'dark', 'dry']
  },
  {
    id: 'ma2',
    category: 'Survival',
    text: "The thing about multiple murder attempts is you start to develop a routine. Check the brakes? Yes. Check the food? Yes. Check behind the shower curtain? Always.",
    tags: ['survival', 'routine', 'dry']
  },
  {
    id: 'ma3',
    category: 'Survival',
    text: "After the fourth time someone tried to kill me, I stopped being scared and started being annoyed. It's like spam calls but with more commitment.",
    tags: ['survival', 'dark', 'satirical']
  },
  {
    id: 'ma4',
    category: 'Survival',
    text: "My therapist says I have trust issues. I told her that's a perfectly rational response to people repeatedly trying to murder you. She took notes.",
    tags: ['survival', 'therapy', 'dry']
  },
  {
    id: 'ma5',
    category: 'Survival',
    text: "People tell me I'm paranoid. I tell them paranoia is just heightened awareness with better marketing. Also, I'm still alive. You're welcome.",
    tags: ['survival', 'paranoia', 'satirical']
  },
  {
    id: 'ma6',
    category: 'Survival',
    text: "The FBI said they couldn't prove anything. I said the hood of my car was filled with sugar. They said that's not illegal. I said it is when it's in the gas tank. They took more notes.",
    tags: ['survival', 'bureaucracy', 'dry']
  },

  // === MILITARY / AVIATION ABSURDITY ===
  {
    id: 'mil1',
    category: 'Military',
    text: "In the Navy, I learned three things: how to fly, how to wait, and how to fill out paperwork about waiting to fly. Two of those skills transfer to civilian life.",
    tags: ['military', 'bureaucracy', 'dry']
  },
  {
    id: 'mil2',
    category: 'Military',
    text: "My flight instructor said, 'If the engine quits, fly the airplane.' My Uber driver said the same thing last Tuesday. Different energy.",
    tags: ['military', 'modern', 'dry']
  },
  {
    id: 'mil3',
    category: 'Military',
    text: "There's a reason they call it an 'ejection seat' and not a 'gentle suggestion seat.' The military doesn't believe in half measures or comfortable furniture.",
    tags: ['military', 'aviation', 'dry']
  },
  {
    id: 'mil4',
    category: 'Military',
    text: "They say war is hell. War is not hell. Hell is warm. War is a Tuesday morning briefing where a PowerPoint slide has 47 bullet points and none of them are the point.",
    tags: ['military', 'bureaucracy', 'satirical']
  },
  {
    id: 'mil5',
    category: 'Military',
    text: "I set a world record in flight hours. My reward was more flight hours. The military's bonus structure is basically recursion.",
    tags: ['military', 'aviation', 'dry']
  },

  // === LETTERS TO EVELYN STYLE ===
  {
    id: 'ev1',
    category: 'Letters',
    text: "Dear Evelyn, I tried explaining cryptocurrency to my neighbor. He now thinks I'm either a genius or a con artist. In fairness, those are the only two options in crypto.",
    tags: ['letters', 'tech', 'witty']
  },
  {
    id: 'ev2',
    category: 'Letters',
    text: "Dear Evelyn, My doctor said I need to reduce stress. I told him about the murder attempts. He prescribed yoga. Yoga, Evelyn. Against murder.",
    tags: ['letters', 'survival', 'dry']
  },
  {
    id: 'ev3',
    category: 'Letters',
    text: "Dear Evelyn, They say what doesn't kill you makes you stronger. After attempt number five, I should be able to bench press a Buick. I cannot. The theory has flaws.",
    tags: ['letters', 'survival', 'satirical']
  },
  {
    id: 'ev4',
    category: 'Letters',
    text: "Dear Evelyn, I went to the DMV today. If you ever want to experience what it feels like to have your soul slowly extracted through your nostrils, I cannot recommend it highly enough.",
    tags: ['letters', 'bureaucracy', 'dry']
  },
  {
    id: 'ev5',
    category: 'Letters',
    text: "Dear Evelyn, My autocorrect changed 'I'm being followed' to 'I'm being fallowed.' If someone is trying to farm me, that explains the manure on my car.",
    tags: ['letters', 'tech', 'absurd']
  },
  {
    id: 'ev6',
    category: 'Letters',
    text: "Dear Evelyn, The lawyer said I have a strong case. The other lawyer said I don't. At $400 an hour each, they agreed to discuss it further. The legal system is just improv with briefcases.",
    tags: ['letters', 'legal', 'satirical']
  },

  // === TECH & MODERN LIFE ===
  {
    id: 'tech1',
    category: 'Technology',
    text: "I built a search engine that finds everything. My wife still says I can't find the mustard in the fridge. Technology has limits.",
    tags: ['tech', 'marriage', 'dry']
  },
  {
    id: 'tech2',
    category: 'Technology',
    text: "AI can now write poetry, compose music, and generate art. It still can't figure out that I don't want to hear about extended car warranties. Progress is selective.",
    tags: ['tech', 'ai', 'satirical']
  },
  {
    id: 'tech3',
    category: 'Technology',
    text: "My smart home knows my schedule, my preferences, and my Spotify habits. It has more personal information than most of my friends. It also doesn't judge my 3 AM cheese habits.",
    tags: ['tech', 'modern', 'dry']
  },

  // === EXISTENTIAL / PHILOSOPHICAL ===
  {
    id: 'ex1',
    category: 'Existential',
    text: "The universe is 13.8 billion years old and I'm spending a fraction of it arguing with people on the internet about whether a hot dog is a sandwich. I regret nothing.",
    tags: ['existential', 'internet', 'dry']
  },
  {
    id: 'ex2',
    category: 'Existential',
    text: "They say you should live every day like it's your last. I tried that. Turns out crying in a Wendy's parking lot is frowned upon before noon.",
    tags: ['existential', 'absurd', 'dry']
  },
  {
    id: 'ex3',
    category: 'Existential',
    text: "I read that the average person walks 100,000 miles in their lifetime. Most of those miles are between the couch and the refrigerator. I'm above average.",
    tags: ['existential', 'lazy', 'dry']
  },

  // === FAMILY / RELATIONSHIPS ===
  {
    id: 'fam1',
    category: 'Family',
    text: "My stepmother tried to poison me. My real mother just uses guilt. I'm not sure which is more effective, but one definitely has a longer half-life.",
    tags: ['family', 'dark', 'dry']
  },
  {
    id: 'fam2',
    category: 'Family',
    text: "Growing up, dinner conversation was mostly about evasive maneuvers. At school, kids talked about sports. I knew twelve ways to exit a building. We were different.",
    tags: ['family', 'military', 'dry']
  },
  {
    id: 'fam3',
    category: 'Family',
    text: "My family reunion is like a UN summit except nobody agrees on borders, the food is worse, and someone always brings up something from 1987 that should have stayed classified.",
    tags: ['family', 'military', 'satirical']
  },

  // === BUREAUCRACY / GOVERNMENT ===
  {
    id: 'bur1',
    category: 'Bureaucracy',
    text: "I reported a murder attempt to the police. They asked if I had a case number. I said I was trying to not have a body number. They asked me to hold.",
    tags: ['bureaucracy', 'survival', 'dry']
  },
  {
    id: 'bur2',
    category: 'Bureaucracy',
    text: "The VA sent me a letter that my file was lost. They found it. Inside the letter. The letter was the file. Government efficiency has achieved sentience.",
    tags: ['bureaucracy', 'military', 'absurd']
  },
  {
    id: 'bur3',
    category: 'Bureaucracy',
    text: "I was on hold with the IRS for two hours. The hold music was 'Don't Worry Be Happy.' I worry. I am not happy. The irony sustains me.",
    tags: ['bureaucracy', 'government', 'dry']
  },

  // === COSMIC / ALIENS (from the book's themes) ===
  {
    id: 'cos1',
    category: 'Cosmic',
    text: "If aliens are watching us from space, they're probably confused why we invented nuclear weapons before we figured out that pineapple doesn't go on pizza. Priorities.",
    tags: ['cosmic', 'food', 'dry']
  },
  {
    id: 'cos2',
    category: 'Cosmic',
    text: "I had a vision that aliens would visit Earth. They did. They looked at Twitter, made a U-turn, and left. Can't blame them.",
    tags: ['cosmic', 'tech', 'satirical']
  },

  // === INFOPILOT META JOKES ===
  {
    id: 'meta1',
    category: 'InfoPilot',
    text: "I built an app called InfoPilot. It searches the internet for you. Just like Google, except with more personality disorders and a book recommendation.",
    tags: ['meta', 'tech', 'dry']
  },
  {
    id: 'meta2',
    category: 'InfoPilot',
    text: "InfoPilot's minimum word count filter is set to 4,000. Because if a website can't commit to 4,000 words, it can't commit to you. We have standards.",
    tags: ['meta', 'standards', 'dry']
  },
  {
    id: 'meta3',
    category: 'InfoPilot',
    text: "Our Educated Individual Sites filter ensures you get comprehensive content. If you wanted a quick blurb, there's always Twitter. We'll wait.",
    tags: ['meta', 'standards', 'satirical']
  },

  // === ADDITIONAL DRY HUMOR ===
  {
    id: 'dry1',
    category: 'Observational',
    text: "I told my barber I wanted to look like a million dollars. He gave me a haircut that costs exactly as much as my student loans. So technically, accurate.",
    tags: ['money', 'dry', 'observational']
  },
  {
    id: 'dry2',
    category: 'Observational',
    text: "My GPS says 'recalculating' with the same tone my mother uses when I make life choices. Disappointed but committed to helping anyway.",
    tags: ['tech', 'family', 'dry']
  },
  {
    id: 'dry3',
    category: 'Observational',
    text: "I bought a self-help book on procrastination. Haven't read it yet. It's been three years. The book is patient. I respect that.",
    tags: ['self-help', 'procrastination', 'dry']
  },
  {
    id: 'dry4',
    category: 'Observational',
    text: "Coffee doesn't ask me stupid questions in the morning. Coffee just shows up and does its job. More people should be like coffee.",
    tags: ['coffee', 'morning', 'dry']
  },
  {
    id: 'dry5',
    category: 'Observational',
    text: "I exercise regularly. By 'regularly,' I mean I regularly think about exercising. Consistency is key.",
    tags: ['exercise', 'procrastination', 'dry']
  },
  {
    id: 'dry6',
    category: 'Observational',
    text: "My neighbor got a drone. Now he can spy on me from 400 feet. Before, he had to use binoculars like a gentleman. Technology ruins everything.",
    tags: ['tech', 'neighbors', 'dry']
  },
  {
    id: 'dry7',
    category: 'Observational',
    text: "I'm not saying I'm old, but my back went out more this year than I did. And my back has terrible taste in restaurants.",
    tags: ['aging', 'dry', 'observational']
  },
  {
    id: 'dry8',
    category: 'Observational',
    text: "The hotel said they had complimentary breakfast. It complimented nothing. The eggs were suspicious. The toast was apologetic. The coffee was in witness protection.",
    tags: ['travel', 'food', 'dry']
  },

  // === ADDITIONAL BATCH — MORE DIVERSIFIED HUMOR ===
  {
    id: 'fp6',
    category: 'Fighter Pilot',
    text: "Dad had a rule: 'Always have an escape plan.' At Thanksgiving, his escape plan was the garage. Mine is pretending my phone is ringing. Evolution takes time.",
    tags: ['aviation', 'family', 'dry']
  },
  {
    id: 'fp7',
    category: 'Fighter Pilot',
    text: "My father could identify an enemy aircraft at twelve miles. I can't identify my own car in a parking garage. The skills didn't transfer. The anxiety did.",
    tags: ['aviation', 'modern', 'dry']
  },
  {
    id: 'ma7',
    category: 'Survival',
    text: "The detective asked if I had any enemies. I handed him a list. He asked if I had it alphabetized. I said it was organized by method of attempt. He requested a copy.",
    tags: ['survival', 'dark', 'dry']
  },
  {
    id: 'ma8',
    category: 'Survival',
    text: "My insurance company called me a 'high-risk individual.' I called them 'masters of understatement.' We agreed to disagree. They raised my premium. I raised my situational awareness.",
    tags: ['survival', 'insurance', 'satirical']
  },
  {
    id: 'mil6',
    category: 'Military',
    text: "The military taught me discipline, resilience, and how to eat a meal in under four minutes. Only one of those skills impresses people at dinner parties. It's not the one you'd think.",
    tags: ['military', 'food', 'dry']
  },
  {
    id: 'mil7',
    category: 'Military',
    text: "The Pentagon has a budget of $800 billion and still can't make a comfortable office chair. If this doesn't explain government spending, nothing will.",
    tags: ['military', 'bureaucracy', 'satirical']
  },
  {
    id: 'ev7',
    category: 'Letters',
    text: "Dear Evelyn, I installed a doorbell camera. It captured forty-seven deliveries, three raccoons, and one person who stood on my porch for twelve minutes then left. The police called it 'inconclusive.' I call it Tuesday.",
    tags: ['letters', 'survival', 'dry']
  },
  {
    id: 'ev8',
    category: 'Letters',
    text: "Dear Evelyn, The airline lost my luggage again. They said it was in Phoenix. I was going to Boston. Someone in Phoenix is now the confused owner of my survival kit. I hope they appreciate the night-vision goggles.",
    tags: ['letters', 'travel', 'absurd']
  },
  {
    id: 'ev9',
    category: 'Letters',
    text: "Dear Evelyn, My accountant asked why I have seventeen shell companies. I said it's because seventeen people have tried to find my address. He said that's not how shell companies work. I said it's working so far.",
    tags: ['letters', 'survival', 'satirical']
  },
  {
    id: 'tech4',
    category: 'Technology',
    text: "My smart fridge told me I'm out of milk. My smart watch told me my heart rate is elevated. My smart speaker told me today's weather. None of them told me someone was in my backyard. 'Smart' is relative.",
    tags: ['tech', 'survival', 'dry']
  },
  {
    id: 'tech5',
    category: 'Technology',
    text: "I asked ChatGPT how to survive a murder attempt. It gave me a twelve-step plan with citations. Step one was 'remain calm.' I was calm before I asked. Now I have eleven more things to worry about.",
    tags: ['tech', 'ai', 'satirical']
  },
  {
    id: 'ex4',
    category: 'Existential',
    text: "I've been told to 'live in the moment.' I tried. The moment contained a suspicious noise from the attic, a car with tinted windows parked outside, and expired yogurt. The moment is overrated.",
    tags: ['existential', 'survival', 'dry']
  },
  {
    id: 'ex5',
    category: 'Existential',
    text: "Scientists say the odds of being born are 1 in 400 trillion. The odds of someone trying to undo that statistic in my case are apparently much higher. Math is humbling.",
    tags: ['existential', 'survival', 'dark']
  },
  {
    id: 'bur4',
    category: 'Bureaucracy',
    text: "I went to renew my driver's license. They asked for two forms of ID, a utility bill, and proof of existence. I showed them six documented murder attempts. They said that only counts as one form.",
    tags: ['bureaucracy', 'survival', 'absurd']
  },
  {
    id: 'bur5',
    category: 'Bureaucracy',
    text: "The HOA sent me a violation notice for my security cameras. Apparently, having fourteen cameras on a single-family home 'affects neighborhood aesthetics.' I sent them my police reports. They tabled the discussion.",
    tags: ['bureaucracy', 'survival', 'satirical']
  },
  {
    id: 'meta4',
    category: 'InfoPilot',
    text: "InfoPilot found 4,244 URLs in one category. That's more reading material than a Barnes & Noble and more useful than half of Wikipedia. We don't aim low. We aim at 4,000 words minimum.",
    tags: ['meta', 'standards', 'dry']
  },
  {
    id: 'meta5',
    category: 'InfoPilot',
    text: "Someone asked why InfoPilot has a minimum word count filter. I said because life's too short for thin content. Then I remembered someone's been trying to make my life shorter. The filter stays.",
    tags: ['meta', 'survival', 'satirical']
  },
  {
    id: 'dry9',
    category: 'Observational',
    text: "I told my dog about my problems. He listened attentively for thirty seconds, then fell asleep. That's still more therapeutic than most of my human conversations.",
    tags: ['pets', 'therapy', 'dry']
  },
  {
    id: 'dry10',
    category: 'Observational',
    text: "My bank has 'bank-level security.' Given what happened to several banks recently, I'm now keeping cash under the mattress. Under a different mattress. At a different address. Standard protocol.",
    tags: ['money', 'survival', 'dry']
  },
  {
    id: 'cos3',
    category: 'Cosmic',
    text: "The universe is expanding. My problems are also expanding. The correlation is unproven but suspicious. Like most things in my life.",
    tags: ['cosmic', 'existential', 'dry']
  },
  // === EXPANDED BATCH 2 - More Dry, Witty, Satirical ===
  {
    id: 'exp01',
    category: 'Technology',
    text: "I asked the AI to find me sources with over 8,000 words. It found three. All were terms of service agreements. Technically correct — the best kind of correct.",
    tags: ['technology', 'dry', 'bureaucracy']
  },
  {
    id: 'exp02',
    category: 'Fighter Pilot',
    text: "My father once ejected at Mach 1.4 and landed in a rice paddy. I once ejected from a Zoom call and landed in a three-hour meeting. Neither of us had a parachute that worked properly.",
    tags: ['aviation', 'technology', 'dry']
  },
  {
    id: 'exp03',
    category: 'Observational',
    text: "The cashier asked if I wanted the receipt. I said no. She gave me a look that suggested I'd just committed a minor federal offense. In her defense, it was Costco.",
    tags: ['observational', 'dry']
  },
  {
    id: 'exp04',
    category: 'Bureaucracy',
    text: "The DMV sent me a letter saying my license renewal was denied because the photo didn't match. The photo was from three months ago. I've apparently aged beyond recognition in one fiscal quarter.",
    tags: ['bureaucracy', 'dry', 'existential']
  },
  {
    id: 'exp05',
    category: 'InfoPilot',
    text: "A user reported that the search engine returned zero results for 'common sense in politics.' We checked the entire internet. The user was correct. Zero results confirmed.",
    tags: ['technology', 'politics', 'dry']
  },
  {
    id: 'exp06',
    category: 'Letters',
    text: "Dear Evelyn, the neighbor's sprinkler went off at 3 AM again. I dove behind the mailbox. Old habits. The mailman saw the whole thing. He's starting to bring my mail to the back door now. Progress.",
    tags: ['letters', 'survival', 'dry']
  },
  {
    id: 'exp07',
    category: 'Military',
    text: "In the military, they said 'hurry up and wait.' In civilian life, they just say 'your appointment is in 45 minutes, please arrive an hour early.' Same energy. Worse chairs.",
    tags: ['military', 'bureaucracy', 'dry']
  },
  {
    id: 'exp08',
    category: 'Existential',
    text: "I set a personal goal to read one book a month. By March I was six books behind. By June, the books had formed a small civilization on my nightstand. They have a leader now. His name is Dostoyevsky.",
    tags: ['existential', 'humor', 'dry']
  },
  {
    id: 'exp09',
    category: 'Cosmic',
    text: "Scientists say there may be billions of habitable planets in our galaxy alone. I can't find a habitable parking spot at Trader Joe's on a Saturday. The universe has its priorities backward.",
    tags: ['cosmic', 'observational', 'dry']
  },
  {
    id: 'exp10',
    category: 'Family',
    text: "My mother called to tell me she found a recipe online. It took 47 minutes. Forty of those were her describing the website's ads, pop-ups, and a video that 'wouldn't stop playing.' The recipe was for toast.",
    tags: ['family', 'technology', 'dry']
  },
  {
    id: 'exp11',
    category: 'Fighter Pilot',
    text: "My father had a callsign. It was classified. Mine is 'Hey You With The Shopping Cart Blocking The Aisle.' Less glamorous. More frequent.",
    tags: ['aviation', 'observational', 'dry']
  },
  {
    id: 'exp12',
    category: 'Technology',
    text: "The smart fridge told me I was out of milk. I told the smart fridge to mind its own business. It ordered milk anyway. I'm now in a power struggle with an appliance. The appliance is winning.",
    tags: ['technology', 'dry', 'existential']
  },
  {
    id: 'exp13',
    category: 'Observational',
    text: "I watched a man parallel park in one attempt on a busy downtown street. Perfect execution. Everyone on the sidewalk started clapping. I'm not joking. It was the most impressive thing I've seen all year and it's December.",
    tags: ['observational', 'dry']
  },
  {
    id: 'exp14',
    category: 'Letters',
    text: "Dear Evelyn, I've been told I need to 'touch grass.' I went outside. The grass was wet. I slipped. The neighbor's security camera caught it. It's now on their family group chat. I've been informed. Warm regards, J.",
    tags: ['letters', 'family', 'dry']
  },
  {
    id: 'exp15',
    category: 'Bureaucracy',
    text: "The IRS sent me a notice that I owed $0.00. They wanted it by April 15th. I'm not sure if this is a test, a trap, or modern art. I'm paying it anyway. With a money order. Certified mail. Registered. Insured.",
    tags: ['bureaucracy', 'paranoid', 'dry']
  },
  {
    id: 'exp16',
    category: 'Military',
    text: "A drill sergeant once told me to 'drop and give him twenty.' I dropped and gave him nineteen. He asked where the twentieth was. I said it was classified. I did a lot of pushups that day.",
    tags: ['military', 'dry']
  },
  {
    id: 'exp17',
    category: 'InfoPilot',
    text: "Our Quality Score algorithm ranks content from 0 to 100. Most of the internet is a 3. We don't tell the websites this. It would hurt their feelings. Websites have feelings now. We checked.",
    tags: ['technology', 'infopilot', 'dry']
  },
  {
    id: 'exp18',
    category: 'Existential',
    text: "I spent four hours organizing my bookmarks into folders. Then I deleted the folders because 'I'll remember where everything is.' I did not remember where everything is. The cycle repeats quarterly.",
    tags: ['existential', 'technology', 'dry']
  },
  {
    id: 'exp19',
    category: 'Cosmic',
    text: "They put a car in space. A Tesla. It's currently orbiting the sun. Meanwhile, my 2004 Camry won't start when it's below 40 degrees. One of us has better priorities. It's not me.",
    tags: ['cosmic', 'technology', 'dry']
  },
  {
    id: 'exp20',
    category: 'Family',
    text: "My uncle said he was 'going off the grid.' He lasted eleven hours. He needed to check the weather. On his phone. On the grid. The grid won. It always wins.",
    tags: ['family', 'technology', 'dry']
  },
  {
    id: 'exp21',
    category: 'Fighter Pilot',
    text: "My father taught me three things: fly fast, fly low, and never let them see you sweat. I drive slowly, live on the fourth floor, and I sweat visibly at job interviews. But I remember the lessons.",
    tags: ['aviation', 'family', 'dry']
  }
];

// =====================================================
// NEWSLETTER CONTENT - Dry, Satirical, Irreverent
// Theme: Surviving absurdity as the son of an elite fighter pilot
// =====================================================
export const NEWSLETTER_SUBJECTS = [
  "CLASSIFIED: This Week's Intelligence Briefing (Not Actually Classified)",
  "SITREP: Your Weekly Status Report from InfoPilot HQ",
  "DEBRIEF: Another Week Survived. Congratulations.",
  "INTEL REPORT: Things We Found While Not Being Murdered",
  "FLIGHT LOG: This Week in Not Crashing and Burning",
  "MISSION BRIEF: Objectives, URLs, and the Continued Existence of the Author",
  "DISPATCH FROM THE COCKPIT: Your InfoPilot Weekly Transmission",
  "AFTER-ACTION REPORT: What Happened, What Didn't Kill Us, What's Next"
];

export const NEWSLETTER_INTROS = [
  "Greetings, Fellow Information Operatives. The author of this newsletter has survived another week, which, given the track record, is statistically noteworthy. Here's what we found while staying alive:",
  
  "Welcome back to another edition of 'How Is He Still Sending These.' Your weekly intelligence briefing follows. All information has been verified to not be poisoned, tampered with, or booby-trapped. Probably.",
  
  "Another seven days. Another zero successful assassination attempts. The streak continues. In celebration, here's what the search engines coughed up this week:",
  
  "This is your captain speaking. We are currently cruising at a reading altitude of 4,000 words minimum, with clear skies and no incoming fire. In the event of an emergency, your nearest exits are the close button and Alt+F4. Let's begin.",
  
  "If you're reading this, congratulations — you're alive. That makes two of us. My father, an elite fighter pilot, taught me that survival is about awareness, adaptability, and never eating anything you didn't watch being prepared. This newsletter has been self-prepared. Proceed with confidence.",
  
  "They say what doesn't kill you makes you stronger. After this week's developments, I should be able to arm-wrestle a grizzly bear. I cannot. But I did find some excellent URLs. Close enough."
];

export const NEWSLETTER_CLOSINGS = [
  "Stay vigilant. Stay informed. Check your brakes. — J. Selman, InfoPilot HQ",
  "Until next time, keep your head on a swivel and your word count above 4,000. Over and out.",
  "This has been your weekly briefing. If you don't hear from me next week, check the usual places. — JS",
  "Remember: in the words of every fighter pilot ever, 'Speed is life.' In the words of every search engine user: 'Where is it?' We're here to help with both.",
  "That's all the intel for this week. The author remains undefeated against all attempts, natural and otherwise. See you next transmission. — JS",
  "Fly safe. Search smart. Trust nothing that loads in under two seconds — it probably doesn't have enough content. Over and out."
];

// =====================================================
// JOKE CATEGORIES for the UI
// =====================================================
export const JOKE_CATEGORIES = [
  { id: 'all', label: 'All Jokes', icon: 'fa-theater-masks' },
  { id: 'Fighter Pilot', label: 'Fighter Pilot', icon: 'fa-fighter-jet' },
  { id: 'Survival', label: 'Survival', icon: 'fa-shield-alt' },
  { id: 'Military', label: 'Military', icon: 'fa-medal' },
  { id: 'Letters', label: 'Letters to Evelyn', icon: 'fa-envelope-open-text' },
  { id: 'Technology', label: 'Technology', icon: 'fa-microchip' },
  { id: 'Existential', label: 'Existential', icon: 'fa-infinity' },
  { id: 'Family', label: 'Family', icon: 'fa-home' },
  { id: 'Bureaucracy', label: 'Government', icon: 'fa-landmark' },
  { id: 'Cosmic', label: 'Cosmic', icon: 'fa-rocket' },
  { id: 'InfoPilot', label: 'InfoPilot', icon: 'fa-search' },
  { id: 'Observational', label: 'Observational', icon: 'fa-eye' }
];

export default JOKES_LIBRARY;
